import { apiClient } from "@/lib/api-client"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import type {
  Sage300ConnectionDto,
  Sage300UpsertRequestDto,
  Sage300UpsertResponseDto,
  Sage300SubmitRequestDto,
  Sage300SubmitResponseDto,
  Sage300SubmissionDto,
  Sage300PayrunDefinition,
  Sage300CompanyDetail,
  Sage300LeaveCode,
  Sage300Bank,
} from "../dtos/Sage300ConnectDto"

export async function getConnection(
  organizationId: string | null,
): Promise<Sage300ConnectionDto | null> {
  try {
    const response = await apiClient.get<Sage300ConnectionDto>(
      `/organizations/${organizationId}/sage300/connection`,
    )
    return response.data
  } catch (err: unknown) {
    const status =
      typeof err === "object" && err !== null
        ? ((err as { response?: { status?: number } }).response?.status ?? 0)
        : 0
    if (status === HttpStatusCodes.NOT_FOUND) return null
    throw err
  }
}

export async function upsertConnection(
  organizationId: string | null,
  dto: Sage300UpsertRequestDto,
): Promise<Sage300UpsertResponseDto> {
  const response = await apiClient.post<Sage300UpsertResponseDto>(
    `/organizations/${organizationId}/sage300/connection`,
    dto,
  )
  return response.data
}

export async function deleteConnection(
  organizationId: string | null,
): Promise<void> {
  await apiClient.delete(`/organizations/${organizationId}/sage300/connection`)
}

export async function getSubmission(
  organizationId: string,
  employeeId: string,
): Promise<Sage300SubmissionDto | null> {
  try {
    const response = await apiClient.get<Sage300SubmissionDto>(
      `/organizations/${organizationId}/sage300/submission/${employeeId}`,
    )
    return response.data
  } catch (err: unknown) {
    const status =
      typeof err === "object" && err !== null
        ? ((err as { response?: { status?: number } }).response?.status ?? 0)
        : 0
    if (status === HttpStatusCodes.NOT_FOUND) return null
    throw err
  }
}

export async function submitToSage300(
  organizationId: string,
  employeeId: string,
  dto: Sage300SubmitRequestDto,
): Promise<Sage300SubmitResponseDto> {
  const response = await apiClient.post<Sage300SubmitResponseDto>(
    `/organizations/${organizationId}/sage300/submit/${employeeId}`,
    dto,
  )
  return response.data
}

export async function getPayrunDefinitions(
  organizationId: string,
): Promise<Sage300PayrunDefinition[]> {
  const response = await apiClient.get<Sage300PayrunDefinition[]>(
    `/organizations/${organizationId}/sage300/payrun-definitions`,
  )
  return response.data
}

export async function getCompanyDetails(
  organizationId: string,
): Promise<Sage300CompanyDetail[]> {
  const response = await apiClient.get<Sage300CompanyDetail[]>(
    `/organizations/${organizationId}/sage300/company-details`,
  )
  return response.data
}

export async function getLeaveCodes(
  organizationId: string,
): Promise<Sage300LeaveCode[]> {
  const response = await apiClient.get<Sage300LeaveCode[]>(
    `/organizations/${organizationId}/sage300/leave-codes`,
  )
  return response.data
}

export async function getBanks(organizationId: string): Promise<Sage300Bank[]> {
  const response = await apiClient.get<Sage300Bank[]>(
    `/organizations/${organizationId}/sage300/banks`,
  )
  return response.data
}

export const sage300ConnectorService = {
  getConnection,
  upsertConnection,
  deleteConnection,
  getSubmission,
  submitToSage300,
  getPayrunDefinitions,
  getCompanyDetails,
  getLeaveCodes,
  getBanks,
}
